import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { getScreenshotData } from 'services/screenshot.service';
import { Grid, Typography, Avatar, Button } from '@mui/material';
import defaultProfileIcon from '../../images/UserProfile.svg';
import '../../scss/statuses.scss';
import classNames from 'classnames';
import { InputText } from 'primereact/inputtext';

const PREFIX = 'ScreenShotTable';

const classes = {
    textEllipsisEffect: `${PREFIX}-textEllipsisEffect`,
    small: `${PREFIX}-small`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`& .${classes.textEllipsisEffect}`]: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},

    [`& .${classes.small}`]: {
		width: theme.spacing(3),
		height: theme.spacing(3),
	}
}));

const ScreenShotTable = (props: any) => {
	const { clientId, screenshotList } = props;

	const [isFilterHidden, setIsFilterHidden] = useState(false);
	const [, setAlertError] = useState({ message: '', open: false });
	//const [screenshotList, setScreenshotList] = useState([]);
	const [tableRows, setTableRows] = useState(10);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(false);
	const [first, setFirst] = useState(0);
	const [totalRecords, setTotalRecords] = useState(0);

	useEffect(() => {
		const getDetails = async () => {
			if (!clientId) return;
			try {
				const screenshotData: any = await getScreenshotData(clientId);
				setTotalRecords(screenshotData.totalElements);
				//setScreenshotList(screenshotData.list);
			} catch (error) {
				const alert = {
					message: 'Unable to fetch screenshot details',
					open: true,
				};
				setAlertError(alert);
				setTimeout(() => {
					const alert = {
						message: '',
						open: false,
					};
					setAlertError(alert);
				}, 2000);
			}
		};
		getDetails();
	}, [clientId]);

	const [sortField, setSortField] = useState('');
	const [sortOrder, setSortOrder] = useState(1);
	const sortMap = new Map([]);
	const getSortRequestArray = (
		inputSortField: any = sortField,
		inputSortOrder: any = sortOrder,
	) => {
		const sortArray: any = [];
		if (inputSortField && inputSortField.length) {
			const object = {
				columnName: sortMap.get(inputSortField)
					? sortMap.get(inputSortField)
					: inputSortField,
				direction: inputSortOrder === -1 ? 'desc' : 'asc',
			};
			sortArray.push(object);
		}

		return sortArray;
	};

	const filterMap = new Map();
	const mapRef = useRef(filterMap);

	useEffect(() => {
		const filterNames = [
			'clientName',
			'advertisementName',
			'campaignName',
			'campaignStatus',
		];

		filterNames.forEach((filterName) => {
			mapRef.current.set(filterName, '');
		});
	}, []);
	const transformStatusToCSSClass = (status: any) => {
		return status.replace(/\s/g, '').toLowerCase();
	};
	const CampaignStatus = (rowData: any) => {
		return (
			<>
				{rowData.campaignStatus ? (
					<>
						<span
							className={classNames(
								'campaign-status',
								'status-' +
									transformStatusToCSSClass(rowData.campaignStatus),
							)}>
							{rowData.campaignStatus}
						</span>
					</>
				) : (
					<> </>
				)}
			</>
		);
	};
	const getFilterRequestArray = () => {
		const requestFilter = [];
		for (const [key, value] of mapRef.current) {
			const requestFilterObject: any = {};
			requestFilterObject.columnName = key;
			requestFilterObject.value = value;
			requestFilterObject.operationType = 'MATCH';
			requestFilter.push(requestFilterObject);
		}

		return requestFilter;
	};

	const generateTextFilter = (filterName: any) => (
		<InputText
			onInput={(e: any) => {
				filterMap.set(filterName, e.target.value);
				update({});
			}}
			className="p-column-filter"
		/>
	);

	const ClientName = (rowData: any, column: any) => {
		return (
			<>
				{rowData[column.field] ? (
					<>
						<Grid container wrap="nowrap" spacing={2}>
							<Grid item>
								<Avatar
									className={classes.small}
									alt={rowData[column.field]}
									src={
										rowData['clientProfilePic']
											? rowData['clientProfilePic']
											: defaultProfileIcon
									}
								/>
							</Grid>
							<Grid item xs zeroMinWidth>
								<Typography noWrap align="left">
									{' '}
									{rowData[column.field].charAt(0).toUpperCase() +
										rowData[column.field].slice(1)}{' '}
								</Typography>
							</Grid>
						</Grid>
					</>
				) : (
					<></>
				)}
			</>
		);
	};

	const Name = (rowData: any, column: any) => {
		return (
			<>
				{rowData[column.field] ? (
					<Typography align="left">
						{' '}
						{rowData[column.field].charAt(0).toUpperCase() +
							rowData[column.field].slice(1)}{' '}
					</Typography>
				) : (
					<></>
				)}
			</>
		);
	};

	const actionBodyTemplate = (rowData: any) => {
		return (
			<Button
				variant="outlined"
				color="primary"
				onClick={props.showScreenshots.bind(null, rowData)}>
				Screenshots
			</Button>
		);
	};

	const [columns] = useState([
		{
			field: 'clientName',
			header: 'Client Name',
			body: ClientName,
			filterElement: generateTextFilter('clientName'),
			filterPlaceHolderText: 'Search by Client Name',
		},
		{
			field: 'campaignName',
			header: 'Campaign Name',
			body: Name,
			filterElement: generateTextFilter('campaignName'),
			filterPlaceHolderText: 'Search by Campaign Name',
		},
		{
			field: 'campaignStatus',
			header: 'Status',
			body: CampaignStatus,
			filterElement: generateTextFilter('campaignStatus'),
			filterPlaceHolderText: 'Search by Campaign Status',
		},
		{
			field: 'advertisementName',
			header: 'Ad Name',
			body: Name,
			filterElement: generateTextFilter('advertisementName'),
			filterPlaceHolderText: 'Search by Advertisement Name',
		},
		{ field: 'screenshotAssets', header: 'Screenshots', body: actionBodyTemplate },
	]);

	const onColumnToggle = (e: any) => {
		const selectedColumns = e.value;
		const orderedSelectedColumns = columns.filter((col: any) =>
			selectedColumns.includes(col),
		);
		setselectedColumns(orderedSelectedColumns);
	};

	const [selectedColumns, setselectedColumns] = useState<any>(columns);

	const columnComponents = selectedColumns?.map((col:any) => {
		return (
			<Column
				key={col.field}
				field={col.field}
				header={col.header}
				body={col.body}
				sortable={false}
				filter={isFilterHidden}
				filterPlaceholder={col.filterPlaceHolderText}
				filterElement={col.filterElement}
				filterMatchMode="custom"
			/>
		);
	});

	const header = (
		<>
			<Root>
				<span style={{ float: 'left' }}>
					<MultiSelect
					placeholder='Select Column'
						value={selectedColumns}
						options={columns}
						optionLabel="header"
						onChange={(e) => onColumnToggle(e)}
						style={{ width: '350px', border: 'none' }}
					/>
				</span>
				<div style={{ textAlign: 'right' }}>
					<FormControlLabel
						control={
							<Checkbox
								name="showFilters"
                style={{ color: '#042630'}}
								onChange={(e) => onFiltersShow(e.target.checked)}
							/>
						}
						label="Show Filters"
					/>
				</div>
			</Root>
		</>
	);

	const update = async (object: any) => {
		setLoading(true);
		let screenshotData: any;
		if (object.sort) {
			screenshotData = await getScreenshotData(
				clientId,
				page,
				tableRows,
				getFilterRequestArray(),
				getSortRequestArray(object.sort.field, object.sort.order),
			);
		} else {
			screenshotData = await getScreenshotData(
				clientId,
				page,
				tableRows,
				getFilterRequestArray(),
				getSortRequestArray(),
			);
		}

		setTotalRecords(screenshotData.totalElements);
		//setScreenshotList(screenshotData.list);
		setLoading(false);
	};

	const onPage = async (event: any) => {
		setLoading(true);
		try {
			const { first: _first, rows } = event;
			const screenshotData: any = await getScreenshotData(
				clientId,
				event.page,
				rows,
				getFilterRequestArray(),
				getSortRequestArray(),
			);
			setFirst(_first);
			setTotalRecords(screenshotData.totalElements);
			//setScreenshotList(screenshotData.list);
			setLoading(false);
			setTableRows(rows);
			setPage(event.page);
		} catch (error) {
			console.error('Error:', error);
			setLoading(false);
		}
	};

	const onFiltersShow = (isFilterShown: any) => {
		setIsFilterHidden(isFilterShown);
	};

	const renderHeader = () => {
		return (
			<div className="mb-4">
				{/* <InputText
					type="search"
					onInput={(e: any) => SetGlobalFilter(e.target.value)}
					placeholder="Global Search"
					className="global-search"
				/> */}
			</div>
		);
	};

	return (
		<div>
			{renderHeader()}
			<DataTable
				resizableColumns
				columnResizeMode="expand"
				value={screenshotList}
				reorderableColumns={true}
				emptyMessage="No records found"
				header={header}
				paginator={true}
				//sortField={sortField}
				//sortOrder={sortOrder}
				// onSort={(e) => {
				// 	setSortField(e.sortField);
				// 	setSortOrder(e.sortOrder);
				// 	update({ sort: { field: e.sortField, order: e.sortOrder } });
				// }}
				paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink
  LastPageLink CurrentPageReport RowsPerPageDropdown"
				currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
				rows={tableRows}
				//lazy
				first={first}
				//onPage={onPage}
				loading={loading}
				totalRecords={totalRecords}
				rowsPerPageOptions={[5, 10, 20]}
				selectionMode="single">
				{columnComponents}
			</DataTable>
		</div>
	);
};

export default ScreenShotTable;
